/**
 * 提醒偏好面板（**N 域** · 用户级）—— 设置页里唯一会写 `user_preferences` 的 UI
 *
 * 依据：N 域 PRD **§5.2 N2「提醒偏好」**（**策略 H 域 / 存储与 UI N 域**）· **§7.2 `AC-N2.20`–`N2.24`** ·
 * 原型 `09-提醒中心_原型.html`（三档 `.level-opt` + 五类 `.type-row` + 免打扰）·
 * `DR-031`（提醒偏好唯一载体 = `user_preferences`）。
 *
 * ## 🔴 本组件兑现了什么
 *
 * `56` 批的提醒中心页面上写着「改强度、按类型开关与免打扰时段，都在「设置」里」——
 * **这句话在本批之前没有落点**（设置页只把它列在"尚未落地"清单里）。
 * 本组件就是那句指路的终点：**同一个用户的同一份偏好，一处写、两处读**。
 *
 * ## 🔴 三条 UI 纪律
 *
 * | # | 纪律 | 本组件怎么做的 |
 * |---|---|---|
 * | ① | **作用域显式标注**（`DR-020` 层级铁律） | 头部带 `ScopeTag「用户级」`+ 一句「家人不受影响」 |
 * | ② | **禁用而不是隐藏** | 免打扰关掉 ⇒ 两个时刻输入框**变灰但仍可见**（隐藏会让人以为没这项） |
 * | ③ | **端侧不写判定** | 「起止相同 / 格式非法」调共享层 `assertDoNotDisturbRange()`；默认值来自服务端 |
 *
 * ## ⚠️ 本批**不含**（诚实登记）
 *
 * - **推送内容预览 / 授权引导**：微信订阅消息**未接入**（小程序端空壳）⇒ 与 `56` 批同一登记；
 * - **「还款日提醒不允许关闭」的高后果强提示**：H 域 PRD §5.3.2 把它列为**待拍板项**（P3）⇒ 未拍板不做。
 */

import { useEffect, useState } from 'react';
import {
  Alert,
  Button,
  Card,
  CardContent,
  Divider,
  FormControlLabel,
  Radio,
  RadioGroup,
  Stack,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { ReminderPreferenceView } from '@jiaying/shared';
import { getPreferences, updatePreferences } from '../api/endpoints.js';
import { ApiError } from '../api/http.js';
import { Loading } from './Loading.js';
import { ScopeTag } from './ScopeTag.js';
import {
  DND_DISABLED_NOTE,
  LEVEL_OPTIONS,
  LEVEL_TITLE,
  NO_CHANGE_LABEL,
  PANEL_NOTE,
  PANEL_SCOPE_NOTE,
  PANEL_TITLE,
  RECOMMENDED_TAG,
  SAVED_LABEL,
  SAVE_LABEL,
  DND_TITLE,
  TYPE_ROWS,
  TYPE_TITLE,
  dndTimezoneNote,
} from '../features/preference-labels.js';
import {
  draftFrom,
  isDirty,
  toPatch,
  validateDraft,
  withTypeToggled,
  type ReminderPreferenceDraft,
} from '../features/preference.js';

export function ReminderPreferencePanel() {
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ['preferences'], queryFn: getPreferences });

  /**
   * 草稿的**用户覆盖值**（`null` = 用户还没动过 ⇒ 用服务端当前值派生）。
   *
   * 🔴 **不能只靠 `useEffect` 建草稿**：`renderToStaticMarkup`（端侧测试基座）
   * **不执行 effect** ⇒ 首屏会永远停在 Loading（本批实测踩到）。
   * ⇒ 渲染期用 `draft ?? draftFrom(服务端值)` **派生**（有预设缓存时首屏就有内容），
   * `useEffect` 只承担「服务端值变了且用户**没有未保存改动**时同步」这一件事。
   */
  const [draftOverride, setDraftOverride] = useState<ReminderPreferenceDraft | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  /**
   * 服务端值 → 草稿。
   *
   * 🔴 **只在"没有未保存改动"时才重建** —— 否则任何一次后台重新取数都会把用户正在编辑的内容
   * **静默冲掉**（`isDirty` 为真说明用户改过，这时以用户为准）。
   */
  useEffect(() => {
    const data = q.data;
    if (data === undefined) return;
    setDraftOverride((prev) =>
      prev === null || !isDirty(data.reminder, prev) ? draftFrom(data.reminder) : prev,
    );
  }, [q.data]);

  const save = useMutation({
    mutationFn: (d: ReminderPreferenceDraft) => updatePreferences(toPatch(d)),
    onSuccess: (res) => {
      setSaved(true);
      setError(null);
      setDraftOverride(draftFrom(res.reminder));
      void qc.invalidateQueries({ queryKey: ['preferences'] });
      // 🔴 顺手让**提醒中心**失效 —— 同一个人刚改完偏好，切过去就该看到新值与新免打扰状态
      void qc.invalidateQueries({ queryKey: ['reminders'] });
    },
    onError: (e: unknown) => {
      setSaved(false);
      setError(e instanceof ApiError ? e.message : String(e));
    },
  });

  if (q.isPending) return <Loading py={2} />;
  if (q.isError) return <Alert severity="error">偏好读取失败：{String(q.error)}</Alert>;

  // 🔴 渲染期派生（见上面 `draftOverride` 的说明）—— `??` 左侧是"用户改过的"
  const draft = draftOverride ?? draftFrom(q.data.reminder);
  const dirty = isDirty(q.data.reminder, draft);
  const localError = validateDraft(draft);

  const submit = (): void => {
    setSaved(false);
    if (localError !== null) {
      setError(localError);
      return;
    }
    save.mutate(draft);
  };

  return (
    <Card data-anchor={PANEL_TITLE} data-testid="reminder-prefs-panel">
      <CardContent>
        <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 0.5 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
            {PANEL_TITLE}
          </Typography>
          <ScopeTag scope="用户级" />
        </Stack>
        <Typography variant="caption" color="text.secondary" component="div">
          {PANEL_NOTE}
        </Typography>
        <Typography variant="caption" color="text.secondary" component="div" data-testid="pref-scope-note">
          {PANEL_SCOPE_NOTE}
        </Typography>

        <Divider sx={{ my: 1.5 }} />

        {/* ─────────── 三档总开关（`AC-N2.20` 默认「常规」） ─────────── */}
        <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
          {LEVEL_TITLE}
        </Typography>
        <RadioGroup
          value={draft.level}
          onChange={(e) => {
            setSaved(false);
            setDraftOverride({ ...draft, level: e.target.value as ReminderPreferenceView['level'] });
          }}
          data-testid="pref-level-group"
        >
          {LEVEL_OPTIONS.map((o) => (
            <FormControlLabel
              key={o.id}
              value={o.id}
              disabled={save.isPending}
              control={<Radio size="small" data-testid={`pref-level-${o.id}`} />}
              label={
                <Stack direction="row" spacing={0.75} alignItems="baseline" flexWrap="wrap" useFlexGap>
                  <Typography variant="body2">{o.label}</Typography>
                  {o.id === 'normal' ? (
                    <Typography variant="caption" color="primary">
                      {RECOMMENDED_TAG}
                    </Typography>
                  ) : null}
                  <Typography variant="caption" color="text.secondary">
                    {o.desc}
                  </Typography>
                </Stack>
              }
            />
          ))}
        </RadioGroup>

        <Divider sx={{ my: 1.5 }} />

        {/* ─────────── 五类按类型开关（`AC-N2.21` 默认全开） ─────────── */}
        <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
          {TYPE_TITLE}
        </Typography>
        <Stack spacing={0.5}>
          {TYPE_ROWS.map((row) => (
            <Stack key={row.id} direction="row" alignItems="center" justifyContent="space-between" spacing={1}>
              <Stack>
                <Typography variant="body2">{row.label}</Typography>
                <Typography variant="caption" color="text.secondary">
                  {row.desc}
                </Typography>
              </Stack>
              <Switch
                size="small"
                disabled={save.isPending}
                checked={draft.typeEnabled[row.id]}
                onChange={(e) => {
                  setSaved(false);
                  setDraftOverride(withTypeToggled(draft, row.id, e.target.checked));
                }}
                data-testid={`pref-type-${row.id}`}
              />
            </Stack>
          ))}
        </Stack>

        <Divider sx={{ my: 1.5 }} />

        {/* ─────────── 免打扰（`AC-N2.22` 默认 22:00–07:30 可改可关） ─────────── */}
        <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1}>
          <Typography variant="body2" sx={{ fontWeight: 600 }}>
            {DND_TITLE}
          </Typography>
          <Switch
            size="small"
            disabled={save.isPending}
            checked={draft.dndEnabled}
            onChange={(e) => {
              setSaved(false);
              setDraftOverride({ ...draft, dndEnabled: e.target.checked });
            }}
            data-testid="pref-dnd-enabled"
          />
        </Stack>

        {/* 🔴 关掉时**禁用而非隐藏**（隐藏会让人以为没这项设置） */}
        <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 0.5 }}>
          <TextField
            type="time"
            size="small"
            label="从"
            value={draft.dndStart}
            disabled={!draft.dndEnabled || save.isPending}
            onChange={(e) => {
              setSaved(false);
              setDraftOverride({ ...draft, dndStart: e.target.value });
            }}
            slotProps={{ htmlInput: { 'data-testid': 'pref-dnd-start' } }}
          />
          <TextField
            type="time"
            size="small"
            label="到"
            value={draft.dndEnd}
            disabled={!draft.dndEnabled || save.isPending}
            onChange={(e) => {
              setSaved(false);
              setDraftOverride({ ...draft, dndEnd: e.target.value });
            }}
            slotProps={{ htmlInput: { 'data-testid': 'pref-dnd-end' } }}
          />
        </Stack>
        <Typography variant="caption" color="text.secondary" component="div" sx={{ mt: 0.5 }} data-testid="pref-dnd-note">
          {draft.dndEnabled ? dndTimezoneNote(q.data.timezone) : DND_DISABLED_NOTE}
        </Typography>

        <Divider sx={{ my: 1.5 }} />

        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
          <Button
            variant="contained"
            size="small"
            onClick={submit}
            disabled={save.isPending || !dirty || localError !== null}
            data-testid="pref-save"
          >
            {SAVE_LABEL}
          </Button>
          {!dirty ? (
            <Typography variant="caption" color="text.secondary">
              {NO_CHANGE_LABEL}
            </Typography>
          ) : null}
          {saved && !dirty ? (
            <Typography variant="caption" color="success.main" data-testid="pref-saved">
              {SAVED_LABEL}
            </Typography>
          ) : null}
        </Stack>

        {/* 🔴 两类错误分开呈现：端侧即时校验（内核给的句子）与服务端返回 */}
        {localError !== null ? (
          <Alert severity="warning" sx={{ mt: 1 }} data-testid="pref-local-error">
            {localError}
          </Alert>
        ) : null}
        {error !== null ? (
          <Alert severity="error" sx={{ mt: 1 }} data-testid="pref-error">
            {error}
          </Alert>
        ) : null}
      </CardContent>
    </Card>
  );
}
